import React from "react";
import useDigitalTheme from "../hooks/useDigitalTheme";
import horizonLogo    from "../../assets/logos/Horizon.png";
import nettoLogo      from "../../assets/logos/NETTO.png";
import digitalLogo    from "../../assets/logos/digitalProf-it.png";
import impulsapLogo   from "../../assets/logos/impulsap.png";

import BarraMenu from "../../components/BarraMenu";
import { httpApiPostText } from "../../lib";
import { useParams } from "react-router";
import RadarChart from "../../components/RadarChart";
import { FaPrint, FaArrowLeft } from "react-icons/fa";
import "./style.css";
import API_HOST, { HEADERS_JSON, METHOD_POST } from "../../ApiConfig";

// ── TODO SonarQube TIPO 4/6: Extraer subcomponentes para reducir complejidad ─
// Candidatos a extraer:
//   - PreguntaRow: renderizado de cada pregunta individual
//   - CategoriaSection: sección de categoría con sus preguntas
//   - useFomatoData(): hook para lógica de carga de datos
// ────────────────────────────────────────────────────────────────────────────


// ── Helper módulo (SonarQube T4: reducir CC de FormatoEvaluacionesPage) ───────

// ── Logo por grupo empresarial ────────────────────────────────────────────────
const GROUP_LOGOS: Record<string, string> = {
  horizon:  horizonLogo,
  netto:    nettoLogo,
  digital:  digitalLogo,
  impulsap: impulsapLogo,
  impulsab: impulsapLogo,
};

const getGroupLogo = (namecompany: string | undefined, client?: string | undefined): string | null => {
  // Buscar logo por nombre de cliente primero
  const tryKey = (val: string | undefined) => {
    if (!val) return null;
    const key = val.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z]/g, '');
    if (key.includes('digital')) return null; // Digital no cuenta como logo de cliente
    return Object.keys(GROUP_LOGOS).find(k => k !== 'digital' && key.includes(k)) || null;
  };
  // Prioridad: client > namecompany > digital
  const matchClient = tryKey(client);
  if (matchClient) return GROUP_LOGOS[matchClient];
  const matchCompany = tryKey(namecompany);
  if (matchCompany) return GROUP_LOGOS[matchCompany];
  return GROUP_LOGOS['digital'];
};

// ── Helpers módulo FormatoEvaluacion (SonarQube T4) ──────────────────────────
const fepParseResponse = (response: any): { ok: boolean; dta?: any; err?: string } => {
  if (!response?.message || typeof response.message !== 'string')
    return { ok: false, err: 'Respuesta inválida del servidor' };
  try {
    const dta = JSON.parse(response.message);
    if (!dta?.payload || !Array.isArray(dta.payload))
      return { ok: false, err: 'Estructura de datos inválida' };
    if (dta.code !== 1) return { ok: false, err: dta.message || 'Error del servidor' };
    return { ok: true, dta };
  } catch {
    return { ok: false, err: 'Respuesta inválida del servidor' };
  }
};

const fepCalcPromedios = (
  cpData: any[], distintos: string[]
): { prom: number[]; categoriesRadar: string[][]; datosRadar: number[][] } => {
  const prom: number[] = []; const cr: string[][] = []; const dr: number[][] = [];
  distintos.forEach((val: string) => {
    let sum=0; let ne=0; const cat: string[]=[]; const dt: number[]=[];
    cpData.filter((d: any) => d.category === val).forEach((q: any) => {
      sum += q.evaluated_range ? parseFloat(q.evaluated_range) : 0;
      if (q.minimum !== q.maximum) { ne++; cat.push(q.title); dt.push(q.evaluated_range); }
    });
    prom.push(ne > 0 ? sum/ne : 0); cr.push([...cat]); dr.push([...dt]);
  });
  return { prom, categoriesRadar: cr, datosRadar: dr };
};


const distinct = (arr: any): any[] => {
  if (!arr || arr.length === 0) return [];
  const uniqueCategories: any = {};
  const arrRes: string[] = [];
  for (const item of arr) {
    if (!uniqueCategories[item.category_id]) {
      uniqueCategories[item.category_id] = true;
      arrRes.push(item.category);
    }
  }
  return arrRes.sort((a, b) => {
    const idA = arr.find((i: any) => i.category === a)?.category_id ?? 0;
    const idB = arr.find((i: any) => i.category === b)?.category_id ?? 0;
    return idA - idB;
  });
};


// ── Custom hook para FormatoEvaluacion (SonarQube T4: sacar lógica del comp) ─
const useFepData = (id: string | undefined, date: string | undefined) => {
  const [data, setData]                   = React.useState<any[]>([]);
  const [cpData, setCpData]               = React.useState<any[]>([]);
  const [distintos, setDistintos]         = React.useState<any[]>([]);
  const [prom, setProm]                   = React.useState<number[]>([]);
  const [datosRadar, setDatosRadar]       = React.useState<any[]>([]);
  const [categoriesRadar, setCat]         = React.useState<any[]>([]);
  const [nombre, setNombre]               = React.useState('');
  const [loading, setLoading]             = React.useState(true);
  const [error, setError]                 = React.useState<string|null>(null);

  const [retry, setRetry] = React.useState(0);
  const refetch = React.useCallback(() => setRetry(r => r + 1), []);

  React.useEffect(() => {
    let promArr: number[]=[]; let crArr: any[]=[]; let drArr: any[]=[];
    let dataVar: any[]=[]; let cpVar: any[]=[]; let distVar: any[]=[];
    (async () => {
      setLoading(true); setError(null);
      try {
        const response = await httpApiPostText(
          API_HOST, METHOD_POST, { ...HEADERS_JSON },
          { valid: "dataGraphics", id_collaborator: `${id}`, id_evaluation: `${date}` }
        );
        const { ok, dta, err } = fepParseResponse(response);
        if (!ok) { setError(err ?? "Error desconocido"); return; }
        dataVar = [...dta.payload].sort((a: any,b: any)=>a.category.localeCompare(b.category));
        cpVar = dataVar; distVar = distinct(cpVar);
        setData([...dataVar]); setCpData([...cpVar]); setDistintos([...distVar]);
        const first = cpVar.find((q: any)=>q.name);
        if (first) setNombre(first.name);
        const { prom: p, categoriesRadar: cr, datosRadar: dr } = fepCalcPromedios(cpVar, distVar);
        promArr=[...p]; crArr=[...cr]; drArr=[...dr];
        setProm(promArr); setCat(crArr); setDatosRadar(drArr);
      } catch { setError("Error al cargar los datos. Por favor intente de nuevo."); }
      finally { setLoading(false); }
    })();
  }, [id, date, retry]);

  return { data, cpData, distintos, prom, datosRadar, categoriesRadar, nombre, loading, error, refetch };
};


// useFepData hook extrae toda la lógica de carga (SonarQube T4: reducir CC)
const FormatoEvaluacionesPage = () => {
  useDigitalTheme();
  const { id, date } = useParams();
  const { data, cpData, distintos, prom, datosRadar,
          categoriesRadar, nombre, loading, error, refetch } = useFepData(id, date);

  return (
    <div className="container-doc bg-white">
      <BarraMenu />

      {/* Loading state */}
      {loading && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 text-muted">Cargando datos de evaluación...</p>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="alert alert-danger mt-4" role="alert">
          <div className="d-flex align-items-center">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <div>
              <h5 className="alert-heading mb-1">Error al cargar los datos</h5>
              <p className="mb-2">{error}</p>
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={refetch}
              >
                <i className="fas fa-redo me-1"></i>
                Reintentar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content - only show when not loading and no error */}
      {!loading && !error && (
        <>
          {/* Cabecera del reporte */}
          <div className="report-header mb-4 mt-3 shadow-sm">
            <div className="d-flex align-items-center mb-2">
              {/* Logo del grupo empresarial — solo aparece el que corresponde */}
              {cpData.length > 0 && (
                <img
                  src={getGroupLogo(cpData[0].namecompany, cpData[0].client) as string}
                  alt={cpData[0].namecompany || cpData[0].client || "Logo"}
                  style={{ height: "52px", maxWidth: "160px", objectFit: "contain", marginRight: "16px" }}
                  className="d-print-block"
                />
              )}
              <h2 className="fw-bold mb-0" style={{ color: "#0a2c5e" }}>
                Evaluación de desempeño{" "}
                {cpData.length > 0 &&
                  cpData[0].period_start_date &&
                  cpData[0].period_end_date && (
                    <span className="text-muted small text-uppercase">
                      ({cpData[0].period_start_date} - {cpData[0].period_end_date})
                    </span>
                  )}
              </h2>
              <div className="ms-auto d-flex gap-2">
                <button
                  className="btn btn-secondary d-print-none"
                  onClick={() => window.history.back()}
                >
                  <FaArrowLeft className="me-1" /> Regresar
                </button>
                <button
                  className="btn btn-primary d-print-none"
                  onClick={() => window.print()}
                >
                  <FaPrint className="me-1" /> Imprimir reporte
                </button>
              </div>
            </div>
            <div className="bg-light p-1 mb-3" style={{ height: "2px" }}></div>

            {/* Información del colaborador */}

            <div className="card shadow-sm border-0 mb-4">
              <div
                className="card-header bg-gradient text-white py-3"
                style={{ backgroundColor: "#1e3a5f" }}
              >
                <div className="d-flex align-items-center">
                  <i className="fas fa-user-circle fs-3 me-2"></i>
                  <h4 className="mb-0 fw-bold">Datos del colaborador</h4>
                </div>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="info-group">
                      <label className="text-muted small text-uppercase">
                        Colaborador
                      </label>
                      <p className="h5 mb-0">{nombre || "No disponible"}</p>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="info-group">
                      <label className="text-muted small text-uppercase">
                        Fecha Ingreso
                      </label>
                      <p className="h5 mb-0">
                        {cpData.length > 0
                          ? cpData[0].user_registered?.slice(0, 10)
                          : "No disponible"}
                      </p>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="info-group">
                      <label className="text-muted small text-uppercase">
                        Líder
                      </label>
                      <p className="h5 mb-0">
                        {cpData.length > 0
                          ? cpData[0].name_leader
                          : "No disponible"}
                      </p>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="info-group">
                      <label className="text-muted small text-uppercase">
                        Cliente
                      </label>
                      <p className="h5 mb-0">
                        {cpData.length > 0 ? cpData[0].client : "No disponible"}
                      </p>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="info-group">
                      <label className="text-muted small text-uppercase">
                        Analista
                      </label>
                      <p className="h5 mb-0">
                        {cpData.length > 0
                          ? cpData[0].make_evaluator
                          : "No disponible"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Resultados por categoría */}
            {[...new Set(distintos)].map((val: any, idx: number) => (
              <div key={idx} className="category-section mb-5">
                <div className="card shadow-sm border-0">
                  <div
                    className="card-header bg-gradient d-flex align-items-center"
                    style={{
                      backgroundColor: "#1e3a5f",
                      borderLeft: "5px solid #17a2b8",
                    }}
                  >
                    <div
                      className="rounded-circle bg-info p-2 d-flex justify-content-center align-items-center me-3"
                      style={{ width: "40px", height: "40px" }}
                    >
                      <i className="fas fa-chart-bar text-white"></i>
                    </div>
                    <h3 className="text-white mb-0 fw-bold">{val}</h3>
                    <div className="ms-auto badge bg-light text-dark px-3 py-2">
                      Promedio:{" "}
                      <span className="fw-bold fs-6 ms-1">
                        {("" + prom[idx]).substring(0, 4)}
                      </span>
                    </div>
                  </div>

                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: "15%" }} className="bg-light">
                              Título
                            </th>
                            <th style={{ width: "45%" }} className="bg-light">
                              Pregunta
                            </th>
                            <th
                              style={{ width: "10%" }}
                              className="bg-light text-center"
                            >
                              Valoración
                            </th>
                            <th style={{ width: "30%" }} className="bg-light">
                              Descripción
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {cpData
                            .filter((dt: any) => dt.category === val)
                            .map((qst: any, qIdx: number) => (
                              <tr key={qIdx}>
                                <td
                                  className="align-middle fw-bold border-start border-info"
                                  style={{ borderWidth: "4px" }}
                                >
                                  {qst.title}
                                </td>
                                <td className="text-wrap">{qst.question}</td>
                                <td className="text-center">
                                  {qst.evaluated_range !== null &&
                                  qst.evaluated_range !== "" ? (
                                    <div className="d-flex justify-content-center">
                                      <div className="badge px-3 py-2 fs-6 bg-success">
                                        {parseFloat(
                                          qst.evaluated_range
                                        ).toFixed(1)}
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-muted">N/A</span>
                                  )}
                                </td>
                                <td className="text-wrap">
                                  {qst.feedback || (
                                    <span className="text-muted fst-italic">
                                      Sin comentarios
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                        {/* <tfoot>
                          <tr className="bg-light">
                            <td colSpan={2} className="fw-bold text-end">
                              Promedio de la categoría:
                            </td>
                            <td className="text-center fw-bold fs-5">
                              {("" + prom[idx]).substring(0, 4)}
                            </td>
                            <td></td>
                          </tr>
                        </tfoot> */}
                      </table>
                    </div>
                  </div>
                </div>

                {/* Gráfico Radar */}
                {datosRadar[idx].some((value: number) => value > 0) && (
                  <div className="mt-4 radar-container shadow-sm border p-3 bg-white rounded">
                    <h5 className="text-center text-muted mb-3">
                      Representación gráfica: {val}
                    </h5>
                    <div className="d-flex justify-content-center">
                      <div style={{ maxWidth: "600px", width: "100%" }}>
                        <RadarChart
                          seriesName={val}
                          seriesData={datosRadar[idx]}
                          categories={categoriesRadar[idx]}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="page-break-after"></div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default FormatoEvaluacionesPage;